import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../context/AuthContext';
import '../auth/auth.css';

// Demo examples that will cycle through
const DEMO_EXAMPLES = [
  {
    prompt: 'Ask Flow to create a 2D platform game ...',
    background: '/login-background.png'
  },
  {
    prompt: 'Flow create a space shot 2d retro game!',
    background: '/space.png'
  },
  {
    prompt: 'Hello Flow, create today a word game with 3 to 10 letters :-)',
    background: '/words.png'
  },
];

/**
 * Login Page Component
 * Beautiful, modern login interface with Mia Platform branding
 */
function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [typedText, setTypedText] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [showBackground, setShowBackground] = useState(false);
  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Already signed in (back from the identity provider onto /login, or a stale tab): straight to Flow, never the form.
  useEffect(() => {
    if (isAuthenticated) { navigate('/', { replace: true }); }
  }, [isAuthenticated, navigate]);

  const currentExample = DEMO_EXAMPLES[currentExampleIndex];

  // Animation loop effect
  useEffect(() => {
    let typingTimeout;
    let loaderTimeout;
    let backgroundTimeout;
    let hideTimeout;
    let restartTimeout;
    
    const startAnimationLoop = () => {
      // Reset states
      setTypedText('');
      setShowLoader(false);
      setShowBackground(false);
      
      // Phase 1: Type the prompt letter by letter (50ms per character)
      let currentIndex = 0;
      const typingDuration = currentExample.prompt.length * 50;
      
      const typeNextChar = () => {
        if (currentIndex <= currentExample.prompt.length) {
          setTypedText(currentExample.prompt.slice(0, currentIndex));
          currentIndex++;
          typingTimeout = setTimeout(typeNextChar, 50);
        } else {
          // Phase 2: Show loader after typing completes
          loaderTimeout = setTimeout(() => {
            setShowLoader(true);
            
            // Phase 3: Show background after 1 second
            backgroundTimeout = setTimeout(() => {
              setShowLoader(false);
              setShowBackground(true);
              
              // Phase 4: Hide and restart after 3 more seconds
              hideTimeout = setTimeout(() => {
                setShowBackground(false);
                
                // Phase 5: Move to next example and restart loop
                restartTimeout = setTimeout(() => {
                  setCurrentExampleIndex((prev) => (prev + 1) % DEMO_EXAMPLES.length);
                  startAnimationLoop();
                }, 500); // Small delay before restarting
              }, 5000); // Stay visible for 5 seconds
            }, 1000); // Loader for 1 second
          }, 500); // Small delay after typing
        }
      };
      
      typeNextChar();
    };
    
    startAnimationLoop();
    
    // Cleanup function
    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(loaderTimeout);
      clearTimeout(backgroundTimeout);
      clearTimeout(hideTimeout);
      clearTimeout(restartTimeout);
    };
  }, [currentExampleIndex]);

  const handleLogin = async () => {
    try {
      setIsLoading(true);
      setError('');

      await login();
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'Failed to start authentication');

      // eslint-disable-next-line no-console
      console.error('Login error:', err);
    }
  };

  return (
    <div className="auth-page-container-split">
      {/* Left Side - Login Form */}
      <div className="auth-left-panel">
        <div className="auth-background">
          {/* Animated gradient background */}
          <div className="gradient-orb gradient-orb-1"></div>
          <div className="gradient-orb gradient-orb-2"></div>
          <div className="gradient-orb gradient-orb-3"></div>
          
          {/* Animated hexagons */}
          <div className="hexagon-container">
            {[...Array(8)].map((_, i) => (
              <div key={i} className={`hexagon hexagon-${i + 1}`}>
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="50 0, 93.301 25, 93.301 75, 50 100, 6.699 75, 6.699 25" />
                </svg>
              </div>
            ))}
          </div>
        </div>
        
        <div className="auth-content">
          <div className="auth-card">
            {/* Logo Section */}
            <div className="auth-logo">
              <div className="logo-circle">
                <svg className="logo-svg" viewBox="0 0 100 100">
                  <path
                    className="logo-path"
                    d="M50 10 L90 30 L90 70 L50 90 L10 70 L10 30 Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <circle className="logo-dot" cx="50" cy="50" fill="currentColor" r="15" />
                </svg>
              </div>
              <h1 className="auth-title">{'Flow'}</h1>
              <p className="auth-subtitle">{'Turn thoughts into code.'}</p>
            </div>

            {/* Login Form */}
            <div className="auth-form">
              <h2 className="form-title">{'Welcome Back'}</h2>
              <p className="form-description">
                {'Sign in with your Mia Platform account to continue'}
              </p>

              {error && (
                <div className="auth-error">
                  <svg className="error-icon" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" fill="none" r="9" stroke="currentColor" strokeWidth="2"/>
                    <path d="M10 6 L10 11" stroke="currentColor" strokeLinecap="round" strokeWidth="2"/>
                    <circle cx="10" cy="14" fill="currentColor" r="1"/>
                  </svg>
                  {error}
                </div>
              )}

              <button
                className="auth-button"
                disabled={isLoading}
                onClick={handleLogin}
              >
                {isLoading ? (
                  <>
                    <span className="button-spinner"></span>
                    <span>{'Connecting...'}</span>
                  </>
                ) : (
                  <>
                    <svg className="button-icon" viewBox="0 0 20 20">
                      <path
                        d="M10 2 L18 10 L10 18 M3 10 L18 10"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                      />
                    </svg>
                    <span>{'Sign in with Mia Platform'}</span>
                  </>
                )}
              </button>

              <div className="auth-divider">
                <span>{'or'}</span>
              </div>

              <button
                className="auth-button-secondary"
                disabled={isLoading}
                onClick={() => navigate('/register')}
              >
                {'Create New Account'}
              </button>
            </div>

            {/* Features Section */}
            <div className="auth-features">
              <div className="feature-item">
                <svg className="feature-icon" viewBox="0 0 20 20">
                  <path
                    d="M10 2 L12 8 L18 8 L13 12 L15 18 L10 14 L5 18 L7 12 L2 8 L8 8 Z"
                    fill="currentColor"
                  />
                </svg>
                <span>{'AI-Powered Development'}</span>
              </div>
              <div className="feature-item">
                <svg className="feature-icon" viewBox="0 0 20 20">
                  <path d="M16 10 A6 6 0 1 1 10 4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                  <path d="M16 4 L16 10 L10 10" fill="currentColor"/>
                </svg>
                <span>{'AI Legacy Modernization'}</span>
              </div>
              <div className="feature-item">
                <svg className="feature-icon" viewBox="0 0 20 20">
                  <path d="M10 2 L4 5 L4 10 C4 14 10 18 10 18 C10 18 16 14 16 10 L16 5 Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                  <path d="M8 10 L9.5 11.5 L12 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                </svg>
                <span>{'Enterprise AI Guardails'}</span>
              </div>
            </div>

            {/* Footer */}
            <div className="auth-footer">
              <p>
                {'By signing in, you agree to our '}
                <a href="https://www.mia-platform.eu/terms" rel="noopener noreferrer" target="_blank">
                  {'Terms of Service'}
                </a>
                {' and '}
                <a href="https://www.mia-platform.eu/privacy" rel="noopener noreferrer" target="_blank">
                  {'Privacy Policy'}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Animated Prompt Example */}
      <div className={`auth-right-panel ${showBackground ? 'show-background' : ''}`} style={showBackground ? {backgroundImage: `url(${currentExample.background})`} : {}}>
        <div className="auth-background">
          {/* Animated gradient background */}
          <div className="gradient-orb gradient-orb-1"></div>
          <div className="gradient-orb gradient-orb-2"></div>
          <div className="gradient-orb gradient-orb-3"></div>
          
          {/* Animated hexagons */}
          <div className="hexagon-container">
            {[...Array(8)].map((_, i) => (
              <div key={i} className={`hexagon hexagon-${i + 1}`}>
                <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="50 0, 93.301 25, 93.301 75, 50 100, 6.699 75, 6.699 25" />
                </svg>
              </div>
            ))}
          </div>
        </div>

        <div className="auth-demo-container">
          <div className="auth-demo-prompt">
            <svg className="prompt-icon-add" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14m-7-7h14" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <svg className="prompt-icon-attach" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input className="prompt-input" type="text" value={typedText} readOnly />
            
            {/* Show loader or send button */}
            {showLoader ? (
              <div className="prompt-loader">
                <div className="loader-spinner"></div>
              </div>
            ) : (
              <>
                <svg className="prompt-icon-mic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4m-4 0h8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <button className="prompt-send-button">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.478 2.405a.75.75 0 00-.926.94l2.432 7.905H13.5a.75.75 0 010 1.5H4.984l-2.432 7.905a.75.75 0 00.926.94 60.519 60.519 0 0018.445-8.986.75.75 0 000-1.218A60.517 60.517 0 003.478 2.405z" />
                  </svg>
                </button>
              </>
            )}
          </div>
          
          {!showBackground && !showLoader && (
            <div className="auth-demo-shortcuts">
              <button className="shortcut-button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 8l4 4m0 0l-4 4m4-4H3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Code</span>
              </button>
              <button className="shortcut-button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Analyze</span>
              </button>
              <button className="shortcut-button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Design</span>
              </button>
              <button className="shortcut-button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Plan</span>
              </button>
              <button className="shortcut-button">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Troubleshoot</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
